// src/index.js
import React from 'react';
import ReactDOM from 'react-dom/client';
import { shouldRetryQuery, queryRetryDelay } from './lib/queryRetry';
import App from './App';
import './styles/index.css';
// Shared chart styling, loaded ONCE here rather than per consumer: these charts
// render on /vendors and the account tabs as well as the board, and a sheet
// imported by one page leaves the same chart unstyled two pages away.
import '@techstack/ui/dist/styles/charts.css';
import { QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { dehydratedQueryShouldPersist } from './lib/queryPersist';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { ThemeProvider } from './contexts/ThemeContext';
import { ConfirmHost } from './components/ConfirmDialog';
import { ToastHost } from './components/Toast';
import { onAppResume } from './platform';

// ─── React Query config ──────────────────────────────────────────────
// staleTime 5min so returning to a backgrounded tab doesn't trigger a
// cascade of "stale" refetches on every active query — that was reading
// to users as the whole page reloading.
// refetchOnWindowFocus / refetchOnReconnect both off for the same reason.
//
// gcTime 24h: TanStack v5 evicts inactive queries from the in-memory
// cache after gcTime. We bump it to 24h so the persister has time to
// flush before eviction; the persister only dehydrates queries still in
// memory at write time.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 24 * 60 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      // Why these are not `retry: 1` — see lib/queryRetry.js.
      retry: shouldRetryQuery,
      retryDelay: queryRetryDelay,
    },
  },
});

// iOS shell only (no-op on web): the OS suspends the webview in the
// background, so refetchOnWindowFocus never fires and timers freeze.
// On foreground resume, mark everything stale — active queries refetch
// in the background while the persisted cache keeps the UI instant.
onAppResume(() => {
  queryClient.invalidateQueries();
});

// ─── Persistent client cache ─────────────────────────────────────────
// localStorage-backed cache for TanStack Query. The dashboard pages
// have many slow endpoints (1-2s each); on the second visit and every
// reload, this serves the last-known-good data instantly while a
// silent refetch happens in the background. Page feels instant.
//
// Bump CACHE_VERSION when a query response shape changes — anything
// persisted under an older buster string is dropped at hydration time
// so stale data with the wrong shape can't render broken UI.
const CACHE_VERSION = 'tsq-v2-2026-05-23';

const persister = createSyncStoragePersister({
  storage: typeof window !== 'undefined' ? window.localStorage : null,
  key: 'tekstak.cache',
  // Throttle writes — TanStack fires events on every mutation; without
  // this the page can stall on rapid query churn.
  throttleTime: 1000,
});

const container = document.getElementById('root') ?? document.createElement('div');
if (!container.id) {
  container.id = 'root';
  document.body.appendChild(container);
}

const root = ReactDOM.createRoot(container);
root.render(
  <React.StrictMode>
    <ThemeProvider>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister,
          // Cache horizon: data older than 24h is dropped at hydration.
          // 24h matches gcTime so we don't keep state the in-memory
          // cache itself would have already evicted.
          maxAge: 24 * 60 * 60 * 1000,
          // Buster string: changing this nukes everything in localStorage
          // under our key. Bump CACHE_VERSION above when query response
          // shapes change in a breaking way.
          buster: CACHE_VERSION,
          dehydrateOptions: {
            shouldDehydrateQuery: dehydratedQueryShouldPersist,
          },
        }}
        onSuccess={() => {
          // Logged once when hydration completes — useful for debugging
          // "is the persisted cache actually loading?" via devtools.
          // eslint-disable-next-line no-console
          console.info('[react-query] hydrated from localStorage');
        }}
      >
        <App />
        <ConfirmHost />
        <ToastHost />
        <ReactQueryDevtools initialIsOpen={false} />
      </PersistQueryClientProvider>
    </ThemeProvider>
  </React.StrictMode>
);
